import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import { ReactComponent as BrandIcon } from '../assets/BrandIcon.svg';
import logo4T from '../assets/4Thought-Technologies.png';

const LAST_UPDATED = 'April 6, 2026';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-semibold text-gray-900 mb-4">{title}</h2>
    <div className="space-y-3 text-gray-600 leading-relaxed">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAuthSession().then(session => {
      setIsSignedIn(!!session.tokens);
    }).catch(() => {});
  }, []);

  const handleSignOut = async () => {
    await amplifySignOut();
    navigate('/login');
  };

  return (
  <div className="min-h-screen bg-white">

    {/* Nav */}
    <header className="border-b border-gray-100">
      <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <BrandIcon className="h-7 w-7" />
          <span className="text-xl font-bold text-gray-900 brand-wordmark">BookMarker</span>
        </Link>
        {isSignedIn
          ? <button onClick={handleSignOut} className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors">Sign Out</button>
          : <Link to="/login" className="text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors">Sign In</Link>
        }
      </div>
    </header>

    {/* Content */}
    <main className="max-w-3xl mx-auto px-6 py-16">

      <div className="mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-3">Privacy Policy</h1>
        <p className="text-sm text-gray-400">Last updated: {LAST_UPDATED}</p>
      </div>

      <Section title="Overview">
        <p>
          BookMarker is operated by 4Thought Technologies, LLC ("we", "us", or "our"). This
          Privacy Policy explains what information we collect when you use BookMarker, how we
          use it, and what choices you have.
        </p>
        <p>
          By using BookMarker you agree to the practices described here. If you do not agree,
          please do not use the service.
        </p>
      </Section>

      <Section title="Information We Collect">
        <p><strong className="text-gray-800">Account information.</strong> When you create an account we collect your email address. Your password is managed securely by our authentication provider and is never stored by us.</p>

        <p><strong className="text-gray-800">Bookmarks.</strong> When you save a bookmark we store the URL, page title, description, tags, categories, summary, and any notes you add. We also record the date saved.</p>

        <p><strong className="text-gray-800">Page content (optional).</strong> The BookMarker browser extension can capture the visible text of a page before you save it. This helps generate summaries for content behind paywalls, login screens, or other access controls our service cannot reach directly. This feature is opt-in — you are shown a clear disclosure when you first use the extension and can enable or disable it at any time using the toggle on the save form. If you opt out, page text is never read from your browser.</p>

        <p><strong className="text-gray-800">Collections.</strong> When you create collections we store the collection name, description, and the list of bookmarks it contains. If you share a collection publicly, it becomes accessible to anyone with the share link.</p>

        <p><strong className="text-gray-800">Preferences.</strong> We store your display name, bio, avatar color, digest preference, and whether your profile is set to public. These are used to personalise your experience.</p>

        <p><strong className="text-gray-800">Usage data.</strong> We collect standard service logs including request timestamps and error information to operate and maintain the service. We do not use third-party analytics trackers in the web application.</p>

        <p><strong className="text-gray-800">Feedback.</strong> If you submit feedback through the app we store the message text and the date submitted.</p>
      </Section>

      <Section title="How We Use Your Information">
        <p><strong className="text-gray-800">To provide the service.</strong> We use your data to save, organise, and display your bookmarks, generate AI-powered summaries and tags, and send your weekly digest email if you have opted in.</p>

        <p><strong className="text-gray-800">AI processing.</strong> Bookmark URLs and page content (when provided) are processed by an AI service to generate summaries, extract tags, and categorise content. This processing happens automatically when a bookmark is saved. This processing is subject to the data processing terms of our AI service provider.</p>

        <p><strong className="text-gray-800">Email communications.</strong> We use your email address to send account-related messages (verification, password reset) and, if you opt in, a weekly digest summarising your recent bookmarks. We do not send marketing emails without your explicit consent.</p>

        <p><strong className="text-gray-800">Service improvement.</strong> Aggregated, anonymised usage data may be used to understand how the service is used and to improve it. We do not sell individual user data.</p>
      </Section>

      <Section title="Data Storage and Security">
        <p>
          Your data is stored on cloud infrastructure in the United States. All data is
          encrypted at rest and in transit using industry-standard encryption.
        </p>
        <p>
          Page content captured by the browser extension is stored alongside the
          corresponding bookmark and used only for AI processing. It is retained as long
          as the bookmark exists and is deleted when you delete the bookmark or your account.
        </p>
        <p>
          While we take reasonable steps to protect your information, no internet-based
          service can guarantee absolute security. Please use a strong, unique password and
          keep your account credentials private.
        </p>
      </Section>

      <Section title="Data Sharing and Third Parties">
        <p>We do not sell, rent, or trade your personal data. We share data only in the following circumstances:</p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li><strong className="text-gray-800">Infrastructure providers.</strong> We use trusted cloud service providers to store and process your data on our behalf. These providers act as data processors under contractual obligations to protect your data.</li>
          <li><strong className="text-gray-800">AI processing.</strong> Bookmark content is processed by an AI service to generate summaries and tags. Only the content you save is processed; we do not share account or identity information with this provider.</li>
          <li><strong className="text-gray-800">Public collections.</strong> If you share a collection via a public link, the collection name, description, and bookmark titles and URLs become visible to anyone with that link.</li>
          <li><strong className="text-gray-800">Public profile.</strong> If you enable a public profile, your display name, bio, and shared collections are visible to anyone with your profile link.</li>
          <li><strong className="text-gray-800">Legal requirements.</strong> We may disclose information if required to do so by law or in response to a valid legal request.</li>
        </ul>
      </Section>

      <Section title="Browser Extension">
        <p>
          The BookMarker browser extension for Chrome and Safari requests the following permissions:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-2">
          <li><strong className="text-gray-800">Current tab access.</strong> Used to read the URL, title, and optionally the text content of the page you are viewing when you click Save. Only the active tab is accessed, and only when you open the extension popup.</li>
          <li><strong className="text-gray-800">Local storage.</strong> Used to save your login session and preferences locally in the browser, including your page content opt-in choice.</li>
          <li><strong className="text-gray-800">Context menu.</strong> Adds a "Save to BookMarker" option to the right-click menu for links. This path does not capture page content.</li>
          <li><strong className="text-gray-800">Notifications.</strong> Used to confirm when a bookmark has been saved via the right-click menu.</li>
        </ul>
        <p className="mt-3">
          The extension does not run in the background, does not monitor your browsing
          history, and does not access any tab other than the one you are actively viewing
          when you open the popup.
        </p>
      </Section>

      <Section title="Your Rights and Choices">
        <p><strong className="text-gray-800">Access and export.</strong> You can export all your bookmarks at any time from the Import/Export page in the app.</p>
        <p><strong className="text-gray-800">Correction.</strong> You can update your account preferences, display name, and bio at any time from your Profile page.</p>
        <p><strong className="text-gray-800">Deletion.</strong> You can delete individual bookmarks or your entire account from the app. Deleting your account permanently removes all your bookmarks, tags, collections, preferences, and feedback from our systems. Account deletion may take up to 30 days to propagate through all backup systems.</p>
        <p><strong className="text-gray-800">Digest opt-out.</strong> You can disable the weekly digest email at any time from your Profile page under Preferences.</p>
        <p><strong className="text-gray-800">Page content opt-out.</strong> You can disable page content capture in the browser extension at any time using the toggle on the save form. Your preference is saved and remembered across sessions.</p>
      </Section>

      <Section title="Data Retention">
        <p>
          We retain your data for as long as your account is active. If you delete your
          account, your personal data is deleted from our primary systems immediately and
          from backup systems within 30 days.
        </p>
        <p>
          Anonymised, aggregated data (such as total bookmark counts used for service
          metrics) may be retained indefinitely as it cannot be linked back to you.
        </p>
      </Section>

      <Section title="Children's Privacy">
        <p>
          BookMarker is not directed at children under 13 years of age. We do not knowingly
          collect personal information from children. If you believe a child has provided us
          with personal information, please contact us and we will delete it promptly.
        </p>
      </Section>

      <Section title="Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. When we make material changes
          we will notify you by email (to the address on your account) and update the "Last
          updated" date at the top of this page. Your continued use of BookMarker after any
          changes constitutes acceptance of the updated policy.
        </p>
      </Section>

      <Section title="Contact Us">
        <p>
          If you have questions about this Privacy Policy or how your data is handled,
          please contact us at:
        </p>
        <address className="not-italic mt-3 p-4 bg-gray-50 rounded-xl border border-gray-200 text-sm">
          <strong className="text-gray-800">4Thought Technologies, LLC</strong><br />
          Email: <a href="mailto:privacy@bookmarker.cloud" className="text-primary-600 hover:underline">privacy@bookmarker.cloud</a>
        </address>
      </Section>

    </main>

    {/* Footer */}
    <footer className="border-t border-gray-100 py-8">
      <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400">
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <img src={logo4T} alt="4Thought Technologies" className="h-6 opacity-70" />
          <span>© {new Date().getFullYear()} 4Thought Technologies, LLC</span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/privacy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms of Service</Link>
          {isSignedIn
            ? <button onClick={handleSignOut} className="hover:text-gray-600 transition-colors">Sign Out</button>
            : <Link to="/login" className="hover:text-gray-600 transition-colors">Sign In</Link>
          }
        </div>
      </div>
    </footer>

  </div>
  );
};

export default PrivacyPolicy;
